import { AudioEngine, type EngineNodes } from "./audio/engine.js";
import { MusicDirector } from "./audio/music.js";
import { VoiceFx } from "./audio/voicefx.js";
import {
  AUDIO_CHUNK_PAUSE_SEC,
  AudioCacheMissError,
  CachedNarrator,
  audioCacheKeys,
  audioKeyParts,
  probeAudioCache,
  resolveAudioCacheOrigin,
} from "./audio/audio-cache.js";
import { playPreparedBuffer } from "./audio/buffer-playback.js";
import { BroadcastNarrator } from "./broadcast/narrator.js";
import { parseTargetedCve, requestEntry, requestSlot, resolveBroadcastOrigin } from "./broadcast/selector.js";
import { NARRATION_VOICES, narrationSpeed, resolveNarrationVoice } from "./audio/voices.js";
import { WebSpeechNarrator, browserSpeechSynthesis } from "./audio/webspeech.js";
import type { Narrator, NegotiationResult, PrepareRequest, PreparedNarration } from "./audio/narrator.js";
import { normalizeCve, type SpeakableScript } from "./cve/normalize.js";
import { ResilientPoolWriter, IndexedDbPoolWriter, type PoolWriter } from "./cve/cache.js";
import { maintainPool, resolvePool, sorted, type CveEntry, type ResolveOptions } from "./cve/pool.js";
import { EMBEDDED_SEEDS } from "./cve/seeds.js";
import { RollingLimiter } from "./cve/fetch.js";
import { createProxiedFetch } from "./cve/proxy.js";
import { pickIndex, resolvePosition, SLOT_MS } from "./schedule/clock.js";
import { PreparationScheduler } from "./schedule/scheduler.js";
import { Player, type PlayerState, type PlayerStatus } from "./schedule/player.js";
import { createUi, type Ui } from "./ui.js";
import { createBackground, type BackgroundHandle } from "./background.js";

/**
 * Composition root.
 *
 * The one place that knows every concrete implementation. It accepts injectable
 * `now`, timers, a narrator factory, a fetch implementation and a pool writer.
 * That seam is what lets tests drive exact 60 s boundaries, inject
 * non-seekable narrators, and capture real `start(when, offset)` calls.
 *
 * The seam is inert unless a test-only flag is present AND the bundle is not a
 * production build. That refusal is asserted by the production smoke test.
 */

export interface CaptureRecord {
  slotIndex: number;
  when: number;
  offsetSec: number;
  entryId: string;
  slotAtCapture: number;
}

export interface TestOverrides {
  now?: () => number;
  scheduleTimer?: (callback: () => void, delayMs: number) => number;
  cancelTimer?: (handle: number) => void;
  narratorFactory?: (context: AudioContext) => Promise<NegotiationResult>;
  fetchImpl?: typeof fetch;
  poolWriter?: PoolWriter;
  capture?: (record: CaptureRecord) => void;
  /** Artificial synthesis latency, so timing behaviour is testable. */
  prepareDelayMs?: number;
  /** Override the wedged-preparation deadline, so tests need not wait minutes. */
  stuckTimeoutMs?: (estimateMs: number) => number;
  /** Override the delay before the station ident plays, so tests need not wait. */
  bumperDelayMs?: number;
  /** Override the music between the ident and the first disclosure. */
  bumperTailMs?: number;
  /** Cache origin for e2e and browser tests; production uses the Vite setting. */
  audioCacheOrigin?: string;
  /** Cache transport, separate from `fetchImpl` so NVD fixtures cannot answer it. */
  audioFetchImpl?: typeof fetch;
  /** Shorten the cache probe so a timeout path is testable. */
  audioProbeBudgetMs?: number;
  /** Override the random quiet gap before the first cached disclosure. */
  cacheStartDelayMs?: number;
  /** Pin the narration engine, so tests do not follow a local `.env`. */
  ttsEngine?: string;
  /** Broadcast origin; production uses the Vite setting. `null` disables it. */
  broadcastOrigin?: string | null;
  /** Shorten the wait for the Worker to produce a clip, so tests stay fast. */
  broadcastWaitMs?: number;
  broadcastPollMs?: number;
}

export interface StationHandle {
  status: () => PlayerStatus;
  slotIndex: () => number | null;
  entryId: () => string | null;
  captures: () => CaptureRecord[];
  seamActive: () => boolean;
  dispose: () => Promise<void>;
}

declare global {
  interface Window {
    __YAV_TEST__?: TestOverrides;
    __YAV__?: StationHandle;
  }
}

export const TEST_HOOK_KEY = "__YAV_TEST__";
export const TEST_FLAG = "test";

/**
 * How long teardown waits for an in-flight `start()` to unwind.
 *
 * Generous enough that a normal startup (engine, pool, negotiation) finishes and
 * disposes cleanly, short enough that a wedged model load cannot hang teardown.
 */
const STARTUP_DISPOSE_GRACE_MS = 5_000;

/**
 * Await `task`, but stop waiting after `ms`. Rejections are swallowed: teardown
 * must not fail because the work it was waiting for did.
 *
 * The losing promise is not cancelled — it may not be cancellable at all — but
 * every consumer of its result rechecks the lifecycle signal, so a straggler is
 * inert.
 */
async function settleWithin(task: Promise<unknown>, ms: number): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const settled = task.then(
    () => undefined,
    () => undefined,
  );
  const deadline = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, ms);
  });
  try {
    await Promise.race([settled, deadline]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/**
 * `import.meta.env.MODE` is statically replaced by Vite, so a production build
 * takes the false branch no matter what the query string says.
 */
export function isTestSeamAllowed(mode: string, search: string): boolean {
  if (mode === "production") return false;
  return new URLSearchParams(search).get(TEST_FLAG) === "1";
}

export interface BootstrapOptions {
  document: Document;
  window: Window;
  overrides?: TestOverrides;
  seamAllowed: boolean;
}

export async function bootstrap(options: BootstrapOptions): Promise<StationHandle> {
  const { document: doc, window: win } = options;
  const overrides: TestOverrides = options.seamAllowed ? (options.overrides ?? {}) : {};
  const now = overrides.now ?? (() => Date.now());
  const captures: CaptureRecord[] = [];
  const scheduleTimer =
    overrides.scheduleTimer ?? ((callback: () => void, delayMs: number) => win.setTimeout(callback, delayMs));
  const cancelTimer =
    overrides.cancelTimer ?? ((handle: number) => win.clearTimeout(handle));

  const engine = new AudioEngine();
  const music = new MusicDirector();
  /** Effects on the narration; built with the engine, fed by the player. */
  let voiceFx: VoiceFx | null = null;
  const writer = overrides.poolWriter ?? new ResilientPoolWriter(new IndexedDbPoolWriter());

  let pool: CveEntry[] = [];
  let narrator: Narrator | null = null;
  let scheduler: PreparationScheduler | null = null;
  let player: Player | null = null;
  let ui: Ui | null = null;
  let started = false;
  let disposed = false;
  let currentSlot: number | null = null;
  let detachVisibility: (() => void) | null = null;
  let lastStatus: PlayerStatus = { state: "idle", slotIndex: null, entryId: null, message: "" };
  /** The entry prepared for a slot, so the panel can show the text that is read. */
  const entriesBySlot = new Map<number, CveEntry>();
  /**
   * The entry chosen before the cache probe, pinned per slot. The pool can grow
   * while the station plays, so the scheduler must reuse the probed entry rather
   * than re-pick; otherwise the probe could key one CVE and playback another.
   */
  const chosenBySlot = new Map<number, CveEntry>();
  /**
   * Where the shared dry-narration cache lives, or null when it is not
   * configured. Null is the default and means no cache request is ever made.
   */
  const cacheOrigin = resolveAudioCacheOrigin(
    win.location.search,
    overrides.audioCacheOrigin ?? import.meta.env.VITE_AUDIO_CACHE_ORIGIN,
  );
  /** Which producer's cache keys this station reads, and how to play them. */
  const narration = resolveNarrationVoice(
    win.location.search,
    // A test may pin the engine: the e2e suite must not follow a developer's
    // local .env, which would change which producer every test exercises.
    overrides.ttsEngine === undefined ? import.meta.env.VITE_TTS_ENGINE : overrides.ttsEngine,
  );
  /**
   * The Worker's broadcast, when configured. This is a different station: the
   * Worker picks the entry, pins it per slot and produces the narration, so the
   * client needs no pool, no picker and no model. Unconfigured keeps the
   * original local path.
   */
  const broadcastOrigin = resolveBroadcastOrigin(
    win.location.search,
    overrides.broadcastOrigin === undefined
      ? import.meta.env.VITE_BROADCAST_ORIGIN
      : (overrides.broadcastOrigin ?? ""),
  );
  let broadcastNarrator: BroadcastNarrator | null = null;
  /**
   * A disclosure named by the URL, played once before the station joins the
   * broadcast. `/CVE-2024-3400` is a link someone can send: it says what the
   * station will say, which the shared slot cannot promise.
   */
  const targetedCve = broadcastOrigin
    ? parseTargetedCve(win.location.pathname, win.location.search)
    : null;
  let cachedNarrator: CachedNarrator | null = null;
  /**
   * The system voice, used only when there is no Worker at all. It cannot seek
   * and it is not the station's voice, but it is better than silence on a
   * build with no broadcast origin.
   */
  let liveNarrator: Narrator | null = null;
  /**
   * A cache hit starts the station with music and no voice for a moment, so the
   * bed's four-second fade-in is actually heard. The ident already uses the same
   * window; a hit should not talk over the top of it.
   */
  let cacheStartGapMs = 0;
  let background: BackgroundHandle | null = null;

  /**
   * One lifecycle signal for the whole station. `dispose()` aborts it, and every
   * await in `start()` rechecks it afterwards: disposing mid-start must not let
   * a late-resolving model load or NVD fetch build a narrator, scheduler, or
   * player on a closed AudioContext.
   */
  const lifecycle = new AbortController();
  /** The in-flight `start()`, so `dispose()` can wait for it to unwind. */
  let startup: Promise<void> | null = null;
  /** The pool fill and maintenance loop, awaited on teardown before the writer closes. */
  let refreshTask: Promise<void> | null = null;
  /** Recurring music-maintenance timer for the music-only (no narrator) mode. */
  let musicTimer: number | null = null;
  /** The pre-rendered station ident, if it is currently playing. */
  let bumper: { source: AudioBufferSourceNode; gain: GainNode } | null = null;
  let bumperStarted = false;
  let bumperTimer: number | null = null;
  /**
   * Resolves when the ident window closes: it ended, it was skipped, or it
   * failed to load. The first disclosure waits on this so the ident is never
   * talked over, which is what made every intro sound cut off.
   */
  let bumperFinished: Promise<void> = Promise.resolve();
  let finishBumper: (() => void) | null = null;
  let narrationStarted = false;
  /** How long the gate waits before the ident, so it lands as a interruption. */
  const BUMPER_DELAY_MIN_MS = 2_500;
  const BUMPER_DELAY_MAX_MS = 6_500;
  /** The same window as the ident, for the first cached disclosure. */
  const CACHE_START_GAP_MIN_MS = 2_500;
  const CACHE_START_GAP_MAX_MS = 6_500;
  /** Music between the ident and the voice, so the ident reads as its own thing. */
  const BUMPER_TAIL_MS = 1_800;
  /** Numbered idents in `public/bumpers`; one is chosen per visit. */
  const BUMPER_COUNT = 12;
  const bumperIndex = 1 + Math.floor(Math.random() * BUMPER_COUNT);

  /**
   * NVD's anonymous quota is per IP, not per call. One limiter per page, so a
   * background refresh and a replacement station's fetch cannot each dispatch
   * five requests inside the same 30 s window.
   */
  const limiter = new RollingLimiter(now);

  /**
   * With a Worker configured, NVD pages go through its shared page cache. The
   * client's limiter then paces only direct-NVD fallbacks; the Worker paces its
   * own upstream fetches. A no-wait limiter keeps `fetchPool` from adding the
   * anonymous quota delay to a page that came from R2.
   */
  const nvdFetch = cacheOrigin
    ? createProxiedFetch({ origin: cacheOrigin, fetchImpl: overrides.fetchImpl, limiter })
    : overrides.fetchImpl;
  const poolLimiter = cacheOrigin ? new RollingLimiter(now, Number.POSITIVE_INFINITY) : limiter;

  ui = createUi(doc, {
    onStart: () => {
      // Retained so `dispose()` can await an in-flight start instead of
      // tearing down resources underneath it.
      startup ??= start();
      void startup;
    },
    onVoiceVolume: (value) => engine.setVoiceVolume(value),
    onMusicVolume: (value) => engine.setMusicVolume(value),
  });

  /**
   * Set once a fetched page has replaced the pool. Updates arrive while the
   * station plays: `entryForSlot` reads `pool` at preparation time, so a larger
   * pool changes only slots not yet prepared. A slot already prepared keeps its
   * narration, so nothing is replayed, skipped or played late.
   */
  let poolUpdated = false;
  let poolFill: Promise<void> | null = null;

  /** Start (or join) the pool fetch. Safe to call before the gate is pressed. */
  function startWeekPool(): Promise<void> {
    if (poolFill) return poolFill;
    if (broadcastOrigin) {
      // The Worker owns the pool. Fetching NVD here would build a pool this
      // station never reads.
      poolFill = Promise.resolve();
      return poolFill;
    }
    const options: ResolveOptions = {
      writer,
      now,
      signal: lifecycle.signal,
      fetchImpl: nvdFetch,
      limiter: poolLimiter,
      // The indicator already says the station is tuning in. Naming the
      // subsystem that is slow was useful when the status line was the only
      // surface; with one dot and one label, changing the words mid-load reads
      // as a state change that did not happen.
      onStatus: () => undefined,
      onPool: (entries) => {
        if (disposed || lifecycle.signal.aborted) return;
        pool = entries;
        poolUpdated = true;
      },
    };

    // Resolves once the pool has something to read; the rest of the window
    // continues behind it. Awaiting the whole fill would put a multi-page
    // download ahead of narration.
    const first = (async () => {
      const resolution = await resolvePool(options);
      if (disposed || lifecycle.signal.aborted) return null;
      // A page persisted while `resolvePool` was returning is newer than its result.
      if (!poolUpdated) pool = resolution.entries;
      return resolution;
    })();
    poolFill = first.then(() => undefined);
    // Retained so teardown can wait for the fill to stop before closing the
    // cache writer.
    refreshTask = (async () => {
      const resolution = await first;
      await resolution?.refreshTask;
      await maintainPool(options);
    })();
    return poolFill;
  }

  const backdropRoot = doc.querySelector(".background-scenes");
  if (backdropRoot) background = createBackground(backdropRoot);

  void startWeekPool().catch(() => undefined);
  if (broadcastOrigin) {
    // Tell the Worker what this listener will hear as soon as the page loads,
    // so the clip is produced while they are still reading the gate copy.
    // Request-only: there is no audio context yet, and nothing to decode.
    void requestSlot(broadcastOrigin, null, { fetchImpl: overrides.audioFetchImpl }).catch(
      () => null,
    );
    if (targetedCve) {
      void requestEntry(broadcastOrigin, targetedCve, {
        fetchImpl: overrides.audioFetchImpl,
      }).catch(() => null);
    }
  }

  function render(state: PlayerState, status: PlayerStatus): void {
    if (!ui) return;
    switch (state) {
      case "idle":
        ui.setState({ state: "idle" });
        return;
      case "loading-model":
        ui.setState({ state: "loading", progress: { percent: 0, label: "Tuning in" } });
        return;
      case "fetching":
        ui.setState({ state: "fetching" });
        return;
      case "error":
        ui.setState({ state: "error", status: status.message });
        return;
      case "suspended":
      case "gate":
      case "playing": {
        // Only the entry the player says is on air right now. A prepared slot
        // that never started speaking has no id in the status, so nothing is
        // shown for it.
        const logged = status.entryId ? (entriesBySlot.get(status.slotIndex ?? -1) ?? null) : null;
        const entry = logged && logged.id === status.entryId ? logged : null;
        ui.setState({
          state: "playing",
          nowPlaying: status.entryId,
          meta: status.message || null,
          slot: status.slotIndex,
          disclosure: entry
            ? {
                id: entry.id,
                description: entry.description,
                severity: entry.severity,
                baseScore: entry.baseScore,
                published: entry.published,
              }
            : null,
        });
        return;
      }
    }
  }

  /**
   * Keep the ambient bed alive independently of narration.
   *
   * The bed has a finite horizon (900 s by default) and must be rebuilt before
   * it runs out, or the sparse event schedule stops and the station quietly
   * degrades to a bare drone. This runs for the whole session — not only in the
   * music-only case — because the bed now starts before narration is ready, and
   * because a narrator can come and go without the bed caring.
   */
  function startMusicMaintenance(nodes: EngineNodes): void {
    if (musicTimer !== null) return;
    const tick = () => {
      if (disposed || lifecycle.signal.aborted) return;
      currentSlot = Math.floor(now() / SLOT_MS);
      music.ensure(nodes.context, nodes.context.currentTime, currentSlot, now());
      voiceFx?.update(nodes.context.currentTime, now());
      musicTimer = scheduleTimer(tick, SLOT_MS);
    };
    tick();
  }

  function preparedFor(slotIndex: number): PreparedNarration | null {
    const ready = scheduler?.readySlot(slotIndex);
    if (!ready || ready.slotIndex !== slotIndex) return null;
    return ready;
  }

  /** Drop the panel text and pins for slots that can no longer be heard. */
  function pruneEntries(slotIndex: number): void {
    for (const slot of entriesBySlot.keys()) {
      if (slot < slotIndex - 4) {
        entriesBySlot.delete(slot);
        // The pin only matters until the slot is prepared and pruned.
        chosenBySlot.delete(slot);
      }
    }
  }

  function entryForSlot(slotIndex: number): CveEntry | null {
    if (pool.length === 0) return null;
    const index = pickIndex(slotIndex, pool.length);
    return index >= 0 ? (pool[index] ?? null) : null;
  }

  /** Open the ident window; the first disclosure waits for it to close. */
  function openBumperWindow(): void {
    bumperFinished = new Promise<void>((resolve) => {
      finishBumper = resolve;
    });
  }

  /** Close the ident window. Idempotent: several paths can end it. */
  function closeBumperWindow(): void {
    const finish = finishBumper;
    finishBumper = null;
    finish?.();
  }

  /**
   * The station ident is pre-rendered, so it needs no model and no inference.
   * It fills the wait between the gate and the first disclosure, and the first
   * disclosure does not begin until it has finished.
   */
  async function playBumper(nodes: EngineNodes): Promise<void> {
    if (bumperStarted) {
      closeBumperWindow();
      return;
    }
    bumperStarted = true;
    try {
      const url = new URL(`bumpers/${bumperIndex}.mp3`, doc.baseURI).toString();
      const response = await fetch(url, { cache: "force-cache" });
      if (!response.ok || disposed) {
        closeBumperWindow();
        return;
      }
      const bytes = await response.arrayBuffer();
      const buffer = await nodes.context.decodeAudioData(bytes);
      if (disposed) {
        closeBumperWindow();
        return;
      }
      const gain = nodes.context.createGain();
      gain.gain.value = 0.9;
      const source = nodes.context.createBufferSource();
      source.buffer = buffer;
      source.connect(gain).connect(nodes.voiceGain);
      source.onended = () => {
        if (bumper?.source === source) bumper = null;
        closeBumperWindow();
      };
      source.start();
      bumper = { source, gain };
      // `onended` is the normal path, but a background tab can throttle timers
      // and a decoded buffer never lies about its own length.
      scheduleTimer(closeBumperWindow, Math.ceil(buffer.duration * 1000) + 250);
    } catch (error) {
      console.warn(
        "station ident unavailable:",
        error instanceof Error ? error.message : error,
      );
      closeBumperWindow();
    }
  }

  function scheduleBumper(nodes: EngineNodes): void {
    if (bumperStarted || bumperTimer !== null) return;
    openBumperWindow();
    const delay =
      overrides.bumperDelayMs ??
      BUMPER_DELAY_MIN_MS + Math.random() * (BUMPER_DELAY_MAX_MS - BUMPER_DELAY_MIN_MS);
    bumperTimer = scheduleTimer(() => {
      bumperTimer = null;
      if (disposed || narrationStarted) {
        closeBumperWindow();
        return;
      }
      void playBumper(nodes);
    }, delay);
  }

  /**
   * Play one requested disclosure before the station joins the broadcast.
   *
   * It goes through the same voice bus and effects as everything else, but it is
   * outside the slot model on purpose: a link that names a CVE promises that
   * CVE, and the shared minute cannot. Nothing is recorded as a capture, because
   * it is not the broadcast.
   */
  async function playPrelude(
    nodes: EngineNodes,
    resolved: { entry: CveEntry; prepared: PreparedNarration },
    voiceBus: AudioNode,
  ): Promise<void> {
    const buffer = resolved.prepared.buffer;
    if (!buffer) return;
    const entry = resolved.entry;
    ui?.setState({
      state: "playing",
      nowPlaying: entry.id,
      meta: null,
      slot: null,
      disclosure: {
        id: entry.id,
        description: entry.description,
        severity: entry.severity,
        baseScore: entry.baseScore,
        published: entry.published,
      },
    });
    await new Promise<void>((resolve) => {
      const source = nodes.context.createBufferSource();
      const gain = nodes.context.createGain();
      gain.gain.value = 0.9;
      source.buffer = buffer;
      source.connect(gain).connect(voiceBus);
      source.onended = () => resolve();
      source.start(nodes.context.currentTime + 0.15);
    });
    if (disposed) return;
    // Back to tuning until the broadcast itself starts speaking.
    ui?.setState({ state: "fetching" });
  }

  /** Cancel a pending ident and fade out one already playing. */
  function cancelBumper(): void {
    if (bumperTimer !== null) {
      cancelTimer(bumperTimer);
      bumperTimer = null;
    }
    stopBumper();
  }

  function stopBumper(): void {
    if (!bumper) {
      closeBumperWindow();
      return;
    }
    const { source, gain } = bumper;
    bumper = null;
    const now = source.context.currentTime;
    gain.gain.cancelScheduledValues(now);
    gain.gain.setTargetAtTime(0, now, 0.08);
    try {
      source.stop(now + 0.4);
    } catch {
      // already stopped
    }
  }

  async function negotiate(context: AudioContext): Promise<NegotiationResult> {
    if (overrides.narratorFactory) return overrides.narratorFactory(context);
    const synth = browserSpeechSynthesis();
    if (synth) {
      const speech = new WebSpeechNarrator(synth);
      try {
        await speech.init();
        return { kind: "system", narrator: speech };
      } catch {
        return { kind: "none", reason: "no-local-voice" };
      }
    }
    return { kind: "none", reason: "unsupported" };
  }

  /**
   * The entry for a slot, pinned the first time it is asked for.
   *
   * `pool` is replaced as pages land, which is desirable for slots that have
   * not been prepared. It is not desirable between the cache probe and the
   * render: a different entry would mean the probe tested one CVE and playback
   * read another. Pinning makes those two agree by construction.
   */
  function chosenEntryForSlot(slotIndex: number): CveEntry | null {
    const pinned = chosenBySlot.get(slotIndex);
    if (pinned) return pinned;
    const entry = entryForSlot(slotIndex);
    if (entry) chosenBySlot.set(slotIndex, entry);
    return entry;
  }

  /**
   * How many seed candidates to probe for a slot.
   *
   * The point of a substitute is to speak at all when the exact entry is not
   * cached, not to survey the corpus: a bounded rotation keeps a miss to a
   * handful of HEADs instead of one per seed.
   */
  const SUBSTITUTE_ATTEMPTS = 3;
  /** Per-slot substitutes, so a slot is probed once and a miss stays a miss. */
  const substituteBySlot = new Map<number, CveEntry | null>();

  /**
   * A cached seed to read instead of the exact slot entry.
   *
   * The user accepted that the broadcast can differ per client here: the clock
   * and the slot stay shared, the CVE does not. The rotation is deterministic,
   * so clients with the same cache still agree.
   */
  async function cachedSubstituteFor(slotIndex: number): Promise<CveEntry | null> {
    const origin = cacheOrigin;
    if (!origin) return null;
    const known = substituteBySlot.get(slotIndex);
    if (known !== undefined) return known;

    const seeds = sorted(EMBEDDED_SEEDS);
    const first = pickIndex(slotIndex, seeds.length);
    let found: CveEntry | null = null;
    for (let offset = 0; offset < Math.min(SUBSTITUTE_ATTEMPTS, seeds.length); offset += 1) {
      const entry = seeds[(first + offset) % seeds.length];
      if (!entry) continue;
      const script = normalizeCve(entry);
      const parts = audioKeyParts(
        narration.engine,
        { text: script.text, chunks: script.chunks },
        narration.engine === "melotts" ? 0 : AUDIO_CHUNK_PAUSE_SEC,
      );
      const keys = await audioCacheKeys(
        {
          entryId: entry.id,
          text: script.text,
          chunks: parts.chunks,
          chunkPauseSec: parts.chunkPauseSec,
          engine: narration.engine,
          voice: narration.id,
          speed: narrationSpeed(narration.engine, entry.id),
        },
        narration.codecs,
      );
      const hits = await Promise.all(
        keys.map(({ key }) =>
          probeAudioCache(origin, key, {
            fetchImpl: overrides.audioFetchImpl,
            budgetMs: overrides.audioProbeBudgetMs,
          }),
        ),
      );
      if (hits.some(Boolean)) {
        found = entry;
        break;
      }
    }
    substituteBySlot.set(slotIndex, found);
    return found;
  }

  /**
   * Ask whether this slot's dry narration is already cached.
   *
   * There is no model to fall back to any more, so this is the only source of
   * the station's own voice. A miss falls to a cached seed or to the system
   * voice, never to a download.
   */
  async function probeCache(slotIndex: number): Promise<{ codec: string; key: string } | null> {
    const origin = cacheOrigin;
    if (!origin) return null;
    const entry = chosenEntryForSlot(slotIndex);
    if (!entry) return null;
    try {
      const script = normalizeCve(entry);
      const parts = audioKeyParts(
        narration.engine,
        { text: script.text, chunks: script.chunks },
        narration.engine === "melotts" ? 0 : AUDIO_CHUNK_PAUSE_SEC,
      );
      const keys = await audioCacheKeys(
        {
          entryId: entry.id,
          text: script.text,
          chunks: parts.chunks,
          chunkPauseSec: parts.chunkPauseSec,
          engine: narration.engine,
          voice: narration.id,
          speed: narrationSpeed(narration.engine, entry.id),
        },
        narration.codecs,
      );
      // Probe every codec concurrently: MP3 first, WAV as the live/legacy
      // fallback. The first hit in preference order wins.
      const hits = await Promise.all(
        keys.map(({ key }) =>
          probeAudioCache(origin, key, {
            fetchImpl: overrides.audioFetchImpl,
            budgetMs: overrides.audioProbeBudgetMs,
          }),
        ),
      );
      const index = hits.findIndex(Boolean);
      return index >= 0 ? (keys[index] ?? null) : null;
    } catch {
      return null;
    }
  }

  /**
   * Resolve one slot's narration.
   *
   * The cache is tried first. On a miss the embedded seeds stand in, because
   * their clips are the only audio guaranteed to exist; otherwise the slot is
   * left to music. A miss throws, which the scheduler treats as a failed
   * prepare, so the slot is retried later and nothing stale is ever played.
   */
  async function prepareNarration(
    entry: CveEntry,
    slotIndex: number,
    signal: AbortSignal,
  ): Promise<{
    prepared: PreparedNarration;
    source: "cache" | "live";
    entry: CveEntry;
    script: SpeakableScript;
  }> {
    const script = normalizeCve(entry);
    const request: PrepareRequest = {
      text: script.text,
      slotIndex,
      entryId: entry.id,
      chunks: script.chunks,
    };
    const cache = cachedNarrator;
    if (cache) {
      try {
        return { prepared: await cache.prepare(request, signal), source: "cache", entry, script };
      } catch (error) {
        if (!(error instanceof AudioCacheMissError)) throw error;
        // Read another cached seed instead, or leave the slot to music. There
        // is nothing to download, so a miss is final for this slot.
        const substitute = await cachedSubstituteFor(slotIndex);
        if (!substitute) throw error;
        const substituteScript = normalizeCve(substitute);
        const substituteRequest: PrepareRequest = {
          text: substituteScript.text,
          slotIndex,
          entryId: substitute.id,
          chunks: substituteScript.chunks,
        };
        return {
          prepared: await cache.prepare(substituteRequest, signal),
          source: "cache",
          entry: substitute,
          script: substituteScript,
        };
      }
    }
    const live = liveNarrator;
    if (!live) throw new Error("no narrator available");
    return { prepared: await live.prepare(request, signal), source: "live", entry, script };
  }

  async function start(): Promise<void> {
    if (started || disposed) return;
    started = true;
    ui?.setSpaceInert(true);

    const aborted = () => disposed || lifecycle.signal.aborted;

    let nodes: EngineNodes;
    try {
      nodes = await engine.ensure();
    } catch (error) {
      if (aborted()) return;
      lastStatus = { state: "error", slotIndex: null, entryId: null, message: "Audio could not start." };
      render("error", lastStatus);
      console.error(error);
      return;
    }
    if (aborted()) return;

    // Music first, always. Nothing that follows should be able to keep the site
    // silent: the whole point is ambient radio, and both remaining steps are
    // slow network work (an NVD page that can trickle for minutes, an 88 MB
    // model download). Starting the bed here means the page makes sound within
    // a click plus an AudioContext resume, and the narration joins it when it is
    // ready.
    music.attach(nodes.musicGain);
    music.ensure(nodes.context, nodes.context.currentTime, Math.floor(now() / SLOT_MS), now());
    scheduleBumper(nodes);
    voiceFx = new VoiceFx(nodes.context, nodes.voiceGain);
    // A slot clock for the bed even while narration is still coming up, so the
    // generative events keep their schedule across the wait.
    startMusicMaintenance(nodes);

    const voiceBus = voiceFx?.input ?? nodes.voiceGain;
    /** The requested disclosure, if the URL named one, played before the join. */
    let prelude: Awaited<ReturnType<BroadcastNarrator["resolveEntry"]>> = null;

    if (broadcastOrigin) {
      // No pool, no picker, no model: ask the Worker what is on air and play the
      // clip it points at. A slot the Worker is still producing is polled for a
      // bounded moment, then left to music rather than to a local download.
      const voice = NARRATION_VOICES.melotts;
      broadcastNarrator = new BroadcastNarrator({
        context: nodes.context,
        voiceBus,
        origin: broadcastOrigin,
        fetchImpl: overrides.audioFetchImpl,
        waitMs: overrides.broadcastWaitMs,
        pollMs: overrides.broadcastPollMs,
      });
      await broadcastNarrator.init();
      if (aborted()) {
        broadcastNarrator.dispose();
        broadcastNarrator = null;
        return;
      }
      voiceFx?.setTrim(voice.trim);
      voiceFx?.setPlain(voice.plain);
      // The bed still gets its fade-in before the first disclosure, exactly as
      // it does for a local cache hit: nothing downloaded in the meantime.
      cacheStartGapMs =
        overrides.cacheStartDelayMs ??
        CACHE_START_GAP_MIN_MS + Math.random() * (CACHE_START_GAP_MAX_MS - CACHE_START_GAP_MIN_MS);
      render("fetching", lastStatus);
      if (targetedCve) {
        // Resolves while the bed fades in and the ident plays; the start gate
        // waits for it just before the broadcast begins.
        prelude = await broadcastNarrator.resolveEntry(targetedCve, lifecycle.signal);
        if (aborted()) return;
      }
    } else {
      render("loading-model", lastStatus);
      // With no cache configured, the model load starts immediately and overlaps
      // the pool fill, exactly as it did before the cache existed. With a cache
      // configured, the entry and the probe have to come first: a hit must never
      // start the download, even by a few hundred milliseconds.
      const negotiationPromise = cacheOrigin ? null : negotiate(nodes.context);
      // The pool fill began at page load, so awaiting it here is usually instant.
      // It has to resolve first: the cache key covers the entry this slot will read.
      await startWeekPool();
      if (aborted()) return;

      const { slotIndex: probeSlot } = resolvePosition(now());
      const cached = await probeCache(probeSlot);
      if (aborted()) return;

      // A miss on the exact entry is not the end: a cached seed can stand in
      // for the slot, which is the only way a build without a Worker speaks.
      const substitute = !cached && cacheOrigin ? await cachedSubstituteFor(probeSlot) : null;
      if (aborted()) return;
      const useCache = Boolean(cacheOrigin) && (Boolean(cached) || substitute !== null);

      if (useCache) {
        cachedNarrator = new CachedNarrator({
          context: nodes.context,
          voiceBus,
          origin: cacheOrigin!,
          fetchImpl: overrides.audioFetchImpl,
          engine: narration.engine,
          voice: narration.id,
          chunkPauseSec: narration.engine === "melotts" ? 0 : AUDIO_CHUNK_PAUSE_SEC,
          codecs: cached
            ? [cached.codec, ...narration.codecs.filter((codec) => codec !== cached.codec)]
            : narration.codecs,
        });
        liveNarrator = null;
        voiceFx?.setTrim(narration.trim);
        voiceFx?.setPlain(narration.plain);
        // Let the bed establish itself before the first disclosure. Only a
        // session that starts from a hit gets this; a miss already spent its
        // silence on the model load and should speak as soon as it can.
        cacheStartGapMs =
          overrides.cacheStartDelayMs ??
          CACHE_START_GAP_MIN_MS + Math.random() * (CACHE_START_GAP_MAX_MS - CACHE_START_GAP_MIN_MS);
        // Nothing is downloading, so the determinate bar is a lie. "Tuning in"
        // without a bar is the honest surface for the gap.
        render("fetching", lastStatus);
      } else {
        const negotiation = negotiationPromise
          ? await negotiationPromise
          : await negotiate(nodes.context);
        // A narrator negotiated after disposal must be torn down here: nobody
        // else holds a reference to it.
        if (aborted()) {
          if (negotiation.kind !== "none") negotiation.narrator.dispose();
          return;
        }
        liveNarrator = negotiation.kind === "none" ? null : negotiation.narrator;
        voiceFx?.setTrim(narration.trim);
        voiceFx?.setPlain(narration.plain);
      }

      if (!liveNarrator && !cachedNarrator) {
        // Honest degradation: music continues, narration does not.
        ui?.setState({ state: "no-voice" });
        return;
      }
    }

    const primary = (liveNarrator ?? cachedNarrator ?? broadcastNarrator)!;
    // Capture the real scheduling arguments. Wrapped before the player is
    // constructed, so the player holds the instrumented narrator. Playback is
    // shared: a cached buffer and a fresh render reach the graph the same way,
    // so a hit is indistinguishable downstream.
    const captureHook = overrides.capture;
    const sourceNarrator: Narrator = {
      id: cachedNarrator ? "cached" : primary.id,
      capabilities: cachedNarrator
        ? { graphRoutable: true, volumeControl: true, seekable: true, duckable: true }
        : primary.capabilities,
      init: () => primary.init(),
      prepare: (request, signal) => primary.prepare(request, signal),
      play: (prepared, playOptions) => {
        narrationStarted = true;
        cancelBumper();
        const active = liveNarrator;
        if (!prepared.buffer && !active) throw new Error("no narrator available");
        const handle = prepared.buffer
          ? playPreparedBuffer(nodes.context, voiceBus, prepared, playOptions)
          : active!.play(prepared, playOptions);
        if (captureHook) {
          const record: CaptureRecord = {
            slotIndex: prepared.slotIndex,
            when: handle.startedAtSec,
            offsetSec: handle.offsetSec,
            entryId: prepared.entryId,
            slotAtCapture: Math.floor(now() / SLOT_MS),
          };
          captures.push(record);
          captureHook(record);
        }
        return handle;
      },
      dispose: () => (liveNarrator ?? cachedNarrator ?? broadcastNarrator)?.dispose(),
    };
    narrator = sourceNarrator;

    const activeNarratorRef = sourceNarrator;

    scheduler = new PreparationScheduler({
      now,
      /**
       * The first prepare aims at the current slot: the estimator has not seen a
       * real duration yet, and waiting for the next boundary is the difference
       * between hearing a disclosure in a few seconds and waiting a minute.
       */
      firstPrepareTargetsCurrent: true,
      /**
       * A cache prepare is fast, so the default eight-slot window would make
       * one hit download eight narrations immediately. One slot of lead is
       * enough to make the next disclosure seamless without turning a hit into
       * a bulk download.
       */
      lookaheadSlots: cachedNarrator || broadcastNarrator ? 1 : undefined,
      /**
       * A prepare is a fetch and a decode, not inference, so the guess starts
       * low; real durations replace it after the first slot either way.
       */
      initialEstimateMs: 1_500,
      prepare: async (slotIndex, signal) => {
        if (broadcastNarrator) {
          // The Worker picks the entry, so there is nothing to choose here.
          const resolved = await broadcastNarrator.resolve(slotIndex, signal);
          if (!resolved) return null;
          entriesBySlot.set(slotIndex, resolved.entry);
          pruneEntries(slotIndex);
          const delay = overrides.prepareDelayMs ?? 0;
          if (delay > 0) {
            await new Promise<void>((resolve) => scheduleTimer(resolve, delay));
            if (signal.aborted) throw new DOMException("aborted", "AbortError");
          }
          return resolved.prepared;
        }
        const entry = chosenEntryForSlot(slotIndex);
        if (!entry) return null;
        entriesBySlot.set(slotIndex, entry);
        pruneEntries(slotIndex);
        const { prepared, entry: usedEntry } = await prepareNarration(entry, slotIndex, signal);
        // A substitute changes which disclosure the panel shows. The exact
        // pool entry was pinned before the probe; the substitute is per-slot.
        entriesBySlot.set(slotIndex, usedEntry);
        const delay = overrides.prepareDelayMs ?? 0;
        if (delay > 0) {
          await new Promise<void>((resolve) => scheduleTimer(resolve, delay));
          // A slot that went stale during the artificial delay must not be
          // reported as ready; the scheduler would discard it anyway, but
          // failing here keeps the duration measurement honest in tests.
          if (signal.aborted) throw new DOMException("aborted", "AbortError");
        }
        return prepared;
      },
      onStatus: (status) => {
        if (status.degraded && lastStatus.state === "playing") {
          ui?.setState({ state: "preparing" });
        }
      },
      onReady: (prepared) => {
        const { slotIndex } = resolvePosition(now());
        if (prepared.slotIndex === slotIndex) player?.evaluate();
      },
      scheduleTimer,
      cancelTimer,
      /**
       * A wedged prepare would otherwise hold the single worker forever. There
       * is no model to rebuild, so recovery just frees the worker.
       */
      recoverStuck: () => Promise.resolve(),
      stuckTimeoutMs: overrides.stuckTimeoutMs,
    });

    const handleStatus = (status: PlayerStatus) => {
      lastStatus = status;
      currentSlot = status.slotIndex;
      render(status.state, status);
    };

    player = new Player({
      now,
      engine: nodes,
      narrator: activeNarratorRef,
      /**
       * Drift is audio position versus wall-clock position. Without this the
       * player would default to the context clock anyway, but stating it here
       * makes the two clocks explicit at the one place that owns the context.
       */
      audioTime: () => nodes.context.currentTime,
      getPrepared: preparedFor,
      requestPreparation: () => scheduler?.pump(),
      onStatus: handleStatus,
      onSlotChange: ({ slotIndex }) => {
        currentSlot = slotIndex;
        scheduler?.noteBoundary();
        music.ensure(nodes.context, nodes.context.currentTime, slotIndex, now());
        voiceFx?.update(nodes.context.currentTime, now());
      },
      scheduleTimer,
      cancelTimer,
    });

    scheduler.pump();
    if (cacheStartGapMs > 0) {
      /*
       * The bed is already playing; hold the first cached disclosure so its
       * four-second fade-in is heard. Then wait for the ident to finish and for
       * a beat of music after it: the two used to be independent timers in the
       * same window, so a short gap clipped the ident mid-sentence. The
       * scheduler may still prepare during all of this, and a boundary crossed
       * inside it is handled by `start()` reading the wall clock fresh.
       */
      scheduleTimer(() => {
        if (aborted()) return;
        void (async () => {
          await bumperFinished;
          if (aborted()) return;
          const tail = overrides.bumperTailMs ?? BUMPER_TAIL_MS;
          await new Promise<void>((resolve) => scheduleTimer(resolve, tail));
          if (aborted()) return;
          if (prelude) {
            await playPrelude(nodes, prelude, voiceBus);
            if (aborted()) return;
          }
          player?.start();
        })();
      }, cacheStartGapMs);
    } else {
      // No quiet gap means narration is already the point; the ident yields.
      player.start();
    }

    /**
     * Background playback is intentional.
     *
     * The station stays on air while the tab is hidden: it is ambient radio, and
     * the normal way to listen is to put it on and go do something else. An
     * earlier version suspended the AudioContext on tab-hide, which made it a
     * foreground-only player.
     *
     * The concern that motivated suspension was real but already covered: hidden
     * tabs have their *timers* throttled, so the boundary callback can fire late.
     * Audio itself is not throttled. A late boundary is safe here because the
     * player re-reads the wall clock immediately before every playback and
     * discards any prepared buffer whose slot has passed — the same "never play
     * stale narration" rule that makes the wall-clock design work — so a late
     * timer costs one slightly late boundary, never a permanent desync.
     *
     * On *return* we still re-sync explicitly: the slot may have rolled over
     * several times while hidden, so re-evaluate at the freshly computed offset
     * and re-arm the boundary timer (which may have been throttled to the point
     * of not having fired at all).
     */
    const onVisibility = () => {
      if (doc.visibilityState === "hidden") return;
      void engine.resume();
      void player?.resume();
      player?.evaluate();
      music.ensure(nodes.context, nodes.context.currentTime, Math.floor(now() / SLOT_MS), now());
      voiceFx?.update(nodes.context.currentTime, now());
    };
    doc.addEventListener("visibilitychange", onVisibility);
    detachVisibility = () => doc.removeEventListener("visibilitychange", onVisibility);
  }

  async function dispose(): Promise<void> {
    if (disposed) return;
    disposed = true;
    // Signal first: every await in `start()` rechecks this, so an in-flight
    // model load or NVD fetch unwinds instead of building a player afterwards.
    lifecycle.abort();

    /**
     * Wait for `start()` to unwind, but never indefinitely.
     *
     * Order matters: disposing a narrator while `start()` is still awaiting it
     * would leave the station pointing at a disposed narrator. But the abort
     * signal can only stop awaits that check it, and the steps that dominate
     * startup — the 88 MB model load, an injected narrator factory, IndexedDB —
     * do not take a signal. Awaiting unconditionally would mean a startup step
     * that never settles blocks teardown forever, leaking the audio context and
     * every node in it. So the wait is bounded, and teardown proceeds regardless;
     * the post-await abort checks in `start()` mean a straggler can no longer
     * build anything on the closed context.
     */
    if (startup) {
      await settleWithin(startup, STARTUP_DISPOSE_GRACE_MS);
    }
    if (musicTimer !== null) {
      cancelTimer(musicTimer);
      musicTimer = null;
    }
    detachVisibility?.();
    cancelBumper();
    // A start waiting on the ident must not outlive the station.
    closeBumperWindow();
    background?.dispose();
    background = null;
    player?.dispose();
    scheduler?.dispose();
    music.dispose();
    voiceFx?.dispose();
    voiceFx = null;
    narrator?.dispose();
    // Also reached when startup unwound before the player wrapper existed.
    broadcastNarrator?.dispose();
    broadcastNarrator = null;
    ui?.dispose();
    // Settle a background refresh before closing the writer it writes through;
    // otherwise it can still touch IndexedDB after teardown.
    if (refreshTask) {
      await settleWithin(refreshTask, STARTUP_DISPOSE_GRACE_MS);
      refreshTask = null;
    }
    writer.close();
    await engine.dispose();
  }

  const handle: StationHandle = {
    status: () => lastStatus,
    slotIndex: () => currentSlot,
    entryId: () => lastStatus.entryId,
    captures: () => captures,
    seamActive: () => options.seamAllowed,
    dispose,
  };
  win.__YAV__ = handle;
  return handle;
}
